import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ActivatedRoute, Router } from '@angular/router';
import { Student } from '../../models/student';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '../../services/log.service';
import _ from 'lodash';
import { CenterService } from '../../services/center.service';
import { Center } from '../../models/center';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { ApiError } from '../../models/apiError';
import { SearchFieldComponent } from '../../components/search-field/search-field.component';
import { AuxiliarService } from '../../services/auxiliar.service';

@Component({
  selector: 'app-center-detail',
  imports: [
    ButtonModule,
    ConfirmPopupModule,
    CommonModule,
    DatePickerModule,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    SearchFieldComponent,
    TableModule,
    ToastModule,
    TooltipModule,
    TranslateModule,
  ],
  providers: [
    ConfirmationService,
  ],
  templateUrl: './center-detail.component.html',
  styleUrl: './center-detail.component.scss',
})
export class CenterDetailComponent implements OnInit {
  @ViewChild('studentsTable', { static: true }) studentsTable!: Table;

  public center!: Center;
  private readonly destroy$ = inject(DestroyRef);
  public editingRowKeys: { [key: string]: boolean } = {};
  public formCenter!: FormGroup;
  public isEditing: boolean = false;
  private newStudentCount: number = 0;
  public students = computed(() => {
    const filter = this.auxiliarService.searchField();
    return _.filter(this.studentsRaw(), (s: Student) => {
      return _.some(['firstname', 'lastname'], (field: string) => {
        return _.includes(_.toLower(_.deburr(_.get(s, field))), filter);
      });
    });
  });
  public studentsCloned: { [id: string]: Student } = {};
  private studentsRaw = signal<Student[]>([]);

  get fC(): { [index: string]: any; } {
    return this.formCenter ? this.formCenter.controls : {};
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private centerService: CenterService,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
    private logService: LogService,
    private router: Router,
    private translate: TranslateService,
  ) {
    this.formCenter = this.formBuilder.group({
      name: ['', Validators.required],
      province: ['', Validators.required],
      town: ['', Validators.required],
      contactPhone: ['', Validators.required],
    })
  }

  ngOnInit(): void {
    const centerId = this.activatedRoute.snapshot.paramMap.get('id');
    if(centerId) this.getCenter(centerId);
  }

  private getCenter(id: string) {
    this.centerService.getCenter(id).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.info('[Center] - getCenter', res);
        this.center = _.get(res, 'body');
        this.formCenter.patchValue(this.center);
        this.studentsRaw.set(_.get(this.center, 'students', []));
        setTimeout(() => {
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.logService.error('[Center] - getCenter', err, this.translate.instant('error.center'));
      }
    })
  }

  public addStudent(): void {
    this.isEditing = true;
    const student = {id: `new-${++this.newStudentCount}`};
    this.studentsRaw.set([student, ...this.studentsRaw()]);
    this.studentsTable.first = 0;
    this.studentsTable.initRowEdit(student);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  public onRowEditInit(student: Student): void {
    this.studentsCloned[student.id as string] = { ...student };
    this.isEditing = true;
  }

  public onRowEditCancel(student: Student, index: number): void {
    this.students()[index] = this.studentsCloned[student.id as string];
    if(_.includes(student.id, 'new-')) {
      const studentsRaw = _.filter(this.studentsRaw(), (student) => !_.includes(student.id, 'new-'));
      this.studentsRaw.set(studentsRaw);
    }
    delete this.studentsCloned[student.id as string];
    this.isEditing = false;
  }

  public onRowEditSave(student: Student): void {
    this.isEditing = false;
  }

  public deleteStudent(event: Event, student: Student): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.student-delete'),
      icon: 'pi pi-info-circle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        _.remove(this.students(), {id: student.id});
        _.remove(this.studentsRaw(), {id: student.id});
      },
      reject: () => {}
    });
  }

  public save(): void {
    this.formCenter.markAllAsTouched();
    if(!this.formCenter.valid) return;
    let centerData: Center = this.formCenter.value;
    centerData.students = this.studentsRaw();
    if(_.get(this.center, 'id')) {
      // update center
      _.forEach(centerData.students, (student: Student) => {
        if(student.id!.startsWith('new')) delete student.id;
      })
      this.centerService.updateCenter(this.center.id!, centerData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Center] - updateCenter', res, this.translate.instant('success.center-update'));
          setTimeout(() => {
            this.router.navigate(['admin', 'centers']);
          });
        },
        error: (err: ApiError) => {
          this.logService.error('[Center] - updateCenter', err, this.translate.instant('error.center-update'));
        }
      });
    } else {
      // new center
      _.forEach(centerData.students, (student: Student) => {
        delete student.id;
      });
      this.centerService.newCenter(centerData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[Center] - newCenter', res, this.translate.instant('success.center-create'));
          setTimeout(() => {
            this.router.navigate(['admin', 'centers']);
          });
        },
        error: (err: ApiError) => {
          this.logService.error('[Center] - newCenter', err, this.translate.instant('error.center-create'));
        }
      });
    }
  }

  public cancel(): void {
    this.router.navigate(['admin', 'centers']);
  }
}
